import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/data`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.detail ||
        data?.message ||
        "Unknown error";
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) e.message = "Your session has expired. Please sign in again.";
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

const DEFAULT_WEIGHTS = { price: 0.30, otif: 0.25, leadTime: 0.20, quality: 0.15, risk: 0.10 };
const SCORE_MIN = 0;
const SCORE_MAX = 100;

/**
 * Compare sourcing options for a specific item across all vendors
 */
export async function compareSourcesForItem(itemNo, { weights = DEFAULT_WEIGHTS, startDate, endDate, company } = {}) {
  const params = {};
  if (weights) params.weights = JSON.stringify(weights);
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (company) params.company = company;

  const response = await api.get(`/sourcing/compare/${encodeURIComponent(itemNo)}`, { params });
  return response.data;
}

/**
 * Get the recommended vendor for an item (quick lookup)
 */
export async function getRecommendedVendor(itemNo) {
  const response = await api.get(`/sourcing/recommend/${encodeURIComponent(itemNo)}`);
  return response.data.recommendedVendor || null;
}

/**
 * Evaluate a specific vendor for an item (detailed breakdown)
 */
export async function evaluateVendorForItem(itemNo, vendorNo, weights = DEFAULT_WEIGHTS) {
  const result = await compareSourcesForItem(itemNo, { weights });
  const source = result.sources?.find((s) => s.vendorNo === vendorNo);
  return source || null;
}

export { DEFAULT_WEIGHTS, SCORE_MIN, SCORE_MAX };
